import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow
} from '../components/ui/table';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogDescription
} from '../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from '../components/ui/select';
import { 
    Gift,
    Plus,
    Minus,
    Trash2,
    Edit,
    Package,
    DollarSign,
    TrendingUp,
    Search,
    RefreshCw,
    Loader2,
    Check,
    X,
    AlertTriangle,
    Upload,
    Image as ImageIcon
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Image upload constants
const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

const CombosPage = () => {
    const [combos, setCombos] = useState([]);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [processing, setProcessing] = useState(false);
    
    // Image upload state
    const [uploading, setUploading] = useState(false);
    const [imagePreview, setImagePreview] = useState(null);
    const fileInputRef = useRef(null);
    
    // Dialogs
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [selectedCombo, setSelectedCombo] = useState(null);
    
    // Form state
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        price: '',
        items: [],
        image_url: ''
    });
    
    // Search
    const [searchQuery, setSearchQuery] = useState('');
    const [productSearch, setProductSearch] = useState('');

    const fetchCombos = useCallback(async () => {
        try {
            const response = await axios.get(`${API_URL}/api/combos?active_only=false`);
            setCombos(response.data);
        } catch (error) {
            console.error('Error fetching combos:', error);
            toast.error('Erro ao carregar combos');
        }
    }, []);

    const fetchProducts = useCallback(async () => {
        try {
            const response = await axios.get(`${API_URL}/api/products`);
            setProducts(response.data);
        } catch (error) {
            console.error('Error fetching products:', error);
        }
    }, []);

    useEffect(() => {
        const loadData = async () => {
            setLoading(true);
            await Promise.all([fetchCombos(), fetchProducts()]);
            setLoading(false);
        };
        loadData();
    }, [fetchCombos, fetchProducts]);

    // Image upload functions
    const uploadToCloudinary = async (file) => {
        try {
            const signatureResponse = await axios.get(`${API_URL}/api/cloudinary/signature?folder=combos`);
            const { signature, timestamp, cloud_name, api_key, folder } = signatureResponse.data;

            const uploadFormData = new FormData();
            uploadFormData.append('file', file);
            uploadFormData.append('api_key', api_key);
            uploadFormData.append('timestamp', timestamp);
            uploadFormData.append('signature', signature);
            uploadFormData.append('folder', folder);

            const uploadResponse = await fetch(
                `https://api.cloudinary.com/v1_1/${cloud_name}/image/upload`,
                { method: 'POST', body: uploadFormData }
            );

            const result = await uploadResponse.json();
            
            if (result.error) {
                throw new Error(result.error.message);
            }

            return result.secure_url;
        } catch (error) {
            console.error('Cloudinary upload error:', error);
            throw error;
        }
    };

    const handleImageSelect = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (!ALLOWED_TYPES.includes(file.type)) {
            toast.error('Formato não suportado. Use JPG, PNG ou WebP');
            return;
        }

        if (file.size > MAX_FILE_SIZE) {
            toast.error('Imagem muito grande. Máximo 2MB');
            return;
        }

        const reader = new FileReader();
        reader.onload = (e) => {
            setImagePreview(e.target.result);
        };
        reader.readAsDataURL(file);

        setUploading(true);
        try {
            const imageUrl = await uploadToCloudinary(file);
            setFormData(prev => ({ ...prev, image_url: imageUrl }));
            toast.success('Imagem enviada com sucesso!');
        } catch (error) {
            toast.error('Erro ao enviar imagem');
            setImagePreview(formData.image_url || null);
        } finally {
            setUploading(false);
        }
    };

    const removeImage = () => {
        setFormData(prev => ({ ...prev, image_url: '' }));
        setImagePreview(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const resetForm = () => {
        setFormData({
            name: '',
            description: '',
            price: '',
            items: [],
            image_url: ''
        });
        setProductSearch('');
        setImagePreview(null);
    };

    const addProductToCombo = (product) => {
        const existingIndex = formData.items.findIndex(item => item.product_id === product.id);
        if (existingIndex >= 0) {
            // Increment quantity
            const newItems = [...formData.items];
            newItems[existingIndex].quantity += 1;
            setFormData({ ...formData, items: newItems });
        } else {
            // Add new item
            setFormData({
                ...formData,
                items: [...formData.items, {
                    product_id: product.id,
                    product_name: product.name,
                    quantity: 1,
                    cost_price: product.cost_price,
                    price: product.price
                }]
            });
        }
        setProductSearch('');
    };

    const updateItemQuantity = (index, delta) => {
        const newItems = [...formData.items];
        newItems[index].quantity += delta;
        if (newItems[index].quantity <= 0) {
            newItems.splice(index, 1);
        }
        setFormData({ ...formData, items: newItems });
    };

    const removeItem = (index) => {
        const newItems = [...formData.items];
        newItems.splice(index, 1);
        setFormData({ ...formData, items: newItems });
    };

    const calculateTotalCost = () => {
        return formData.items.reduce((sum, item) => sum + (item.cost_price * item.quantity), 0);
    };

    const calculateTotalValue = () => {
        return formData.items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    };

    const handleCreate = async () => {
        if (!formData.name.trim()) {
            toast.error('Informe o nome do combo');
            return;
        }
        if (!formData.price || parseFloat(formData.price) <= 0) {
            toast.error('Informe o preço do combo');
            return;
        }
        if (formData.items.length === 0) {
            toast.error('Adicione pelo menos um produto ao combo');
            return;
        }

        const totalCost = calculateTotalCost();
        if (parseFloat(formData.price) <= totalCost) {
            toast.error(`Preço deve ser maior que o custo (R$ ${totalCost.toFixed(2)})`);
            return;
        }

        setProcessing(true);
        try {
            await axios.post(`${API_URL}/api/combos`, {
                name: formData.name,
                description: formData.description,
                price: parseFloat(formData.price),
                items: formData.items.map(({ product_id, product_name, quantity }) => ({
                    product_id,
                    product_name,
                    quantity
                })),
                image_url: formData.image_url || null
            });
            toast.success('Combo criado com sucesso!');
            setCreateDialogOpen(false);
            resetForm();
            fetchCombos();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erro ao criar combo');
        } finally {
            setProcessing(false);
        }
    };

    const handleEdit = async () => {
        if (!formData.name.trim() || !formData.price || formData.items.length === 0) {
            toast.error('Preencha todos os campos obrigatórios');
            return;
        }

        const totalCost = calculateTotalCost();
        if (parseFloat(formData.price) <= totalCost) {
            toast.error(`Preço deve ser maior que o custo (R$ ${totalCost.toFixed(2)})`);
            return;
        }

        setProcessing(true);
        try {
            await axios.put(`${API_URL}/api/combos/${selectedCombo.id}`, {
                name: formData.name,
                description: formData.description,
                price: parseFloat(formData.price),
                items: formData.items.map(({ product_id, product_name, quantity }) => ({
                    product_id,
                    product_name,
                    quantity
                })),
                image_url: formData.image_url || null
            });
            toast.success('Combo atualizado!');
            setEditDialogOpen(false);
            resetForm();
            fetchCombos();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erro ao atualizar combo');
        } finally {
            setProcessing(false);
        }
    };

    const handleDelete = async () => {
        if (!selectedCombo) return;

        setProcessing(true);
        try {
            await axios.delete(`${API_URL}/api/combos/${selectedCombo.id}`);
            toast.success('Combo desativado!');
            setDeleteDialogOpen(false);
            setSelectedCombo(null);
            fetchCombos();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erro ao desativar combo');
        } finally {
            setProcessing(false);
        }
    };

    const handleToggleActive = async (combo) => {
        try {
            await axios.put(`${API_URL}/api/combos/${combo.id}`, {
                active: !combo.active
            });
            toast.success(combo.active ? 'Combo desativado' : 'Combo ativado');
            fetchCombos();
        } catch (error) {
            toast.error('Erro ao alterar status');
        }
    };

    const openEditDialog = (combo) => {
        setSelectedCombo(combo);
        setFormData({
            name: combo.name,
            description: combo.description || '',
            price: combo.price.toString(),
            items: combo.items.map(item => {
                const product = products.find(p => p.id === item.product_id);
                return {
                    ...item,
                    cost_price: product?.cost_price || 0,
                    price: product?.price || 0
                };
            }),
            image_url: combo.image_url || ''
        });
        setImagePreview(combo.image_url || null);
        setEditDialogOpen(true);
    };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
    };

    const filteredCombos = combos.filter(combo => 
        combo.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const filteredProducts = products.filter(product =>
        product.name.toLowerCase().includes(productSearch.toLowerCase()) &&
        !formData.items.some(item => item.product_id === product.id)
    );

    if (loading) {
        return (
            <div className="flex items-center justify-center h-96">
                <div className="w-12 h-12 border-4 border-rr-yellow-500 border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-fade-in-up" data-testid="combos-page">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-heading font-bold text-white">Combos</h1>
                    <p className="text-gray-400 mt-1">Gerencie combos promocionais</p>
                </div>
                <div className="flex items-center gap-3">
                    <Button
                        variant="outline"
                        size="sm"
                        className="border-[#262626] text-gray-400"
                        onClick={fetchCombos}
                    >
                        <RefreshCw className="w-4 h-4" />
                    </Button>
                    <Button
                        className="bg-rr-yellow-500 hover:bg-rr-yellow-400 text-black font-bold"
                        onClick={() => { resetForm(); setCreateDialogOpen(true); }}
                    >
                        <Plus className="w-4 h-4 mr-2" />
                        Novo Combo
                    </Button>
                </div>
            </div>

            {/* Search */}
            <div className="relative max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <Input
                    placeholder="Buscar combos..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 bg-[#121212] border-[#262626] text-white"
                />
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="bg-[#121212] border-[#262626]">
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-md bg-rr-yellow-500/20 flex items-center justify-center">
                                <Gift className="w-5 h-5 text-rr-yellow-500" />
                            </div>
                            <div>
                                <p className="text-xs text-gray-500 uppercase">Total Combos</p>
                                <p className="text-2xl font-bold text-white">{combos.length}</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card className="bg-[#121212] border-[#262626]">
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-md bg-emerald-500/20 flex items-center justify-center">
                                <Check className="w-5 h-5 text-emerald-500" />
                            </div>
                            <div>
                                <p className="text-xs text-gray-500 uppercase">Ativos</p>
                                <p className="text-2xl font-bold text-emerald-500">{combos.filter(c => c.active).length}</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card className="bg-[#121212] border-[#262626]">
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-md bg-purple-500/20 flex items-center justify-center">
                                <TrendingUp className="w-5 h-5 text-purple-500" />
                            </div>
                            <div>
                                <p className="text-xs text-gray-500 uppercase">Margem Média</p>
                                <p className="text-2xl font-bold text-purple-500">
                                    {combos.length > 0 
                                        ? `${(combos.reduce((s, c) => s + (c.profit_percentage || 0), 0) / combos.length).toFixed(0)}%`
                                        : '0%'
                                    }
                                </p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Combos Table */}
            <Card className="bg-[#121212] border-[#262626]">
                <CardContent className="p-0">
                    <Table>
                        <TableHeader>
                            <TableRow className="border-[#262626] hover:bg-transparent">
                                <TableHead className="text-gray-400">Combo</TableHead>
                                <TableHead className="text-gray-400">Itens</TableHead>
                                <TableHead className="text-gray-400 text-right">Custo</TableHead>
                                <TableHead className="text-gray-400 text-right">Preço</TableHead>
                                <TableHead className="text-gray-400 text-right">Lucro</TableHead>
                                <TableHead className="text-gray-400 text-center">Status</TableHead>
                                <TableHead className="text-gray-400 w-24"></TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {filteredCombos.map(combo => (
                                <TableRow key={combo.id} className="border-[#262626] hover:bg-white/5">
                                    <TableCell>
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 bg-purple-500/20 rounded-md flex items-center justify-center">
                                                <Gift className="w-5 h-5 text-purple-500" />
                                            </div>
                                            <div>
                                                <p className="text-white font-medium">{combo.name}</p>
                                                {combo.description && (
                                                    <p className="text-xs text-gray-500 truncate max-w-[200px]">{combo.description}</p>
                                                )}
                                            </div>
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        <div className="flex flex-wrap gap-1">
                                            {combo.items.map((item, idx) => (
                                                <Badge key={idx} variant="outline" className="border-[#333] text-gray-400 text-xs">
                                                    {item.quantity}x {item.product_name.split(' ').slice(0, 2).join(' ')}
                                                </Badge>
                                            ))}
                                        </div>
                                    </TableCell>
                                    <TableCell className="text-right text-gray-400">
                                        {formatCurrency(combo.total_cost)}
                                    </TableCell>
                                    <TableCell className="text-right text-white font-medium">
                                        {formatCurrency(combo.price)}
                                    </TableCell>
                                    <TableCell className="text-right">
                                        <div>
                                            <span className="text-emerald-500 font-medium">{formatCurrency(combo.profit_margin)}</span>
                                            <span className="text-xs text-gray-500 ml-1">({combo.profit_percentage?.toFixed(0)}%)</span>
                                        </div>
                                    </TableCell>
                                    <TableCell className="text-center">
                                        <Badge 
                                            className={combo.active 
                                                ? 'bg-emerald-500/20 text-emerald-500 border-emerald-500/30 cursor-pointer' 
                                                : 'bg-red-500/20 text-red-500 border-red-500/30 cursor-pointer'
                                            }
                                            onClick={() => handleToggleActive(combo)}
                                        >
                                            {combo.active ? 'Ativo' : 'Inativo'}
                                        </Badge>
                                    </TableCell>
                                    <TableCell>
                                        <div className="flex items-center gap-2">
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                className="text-gray-400 hover:text-white"
                                                onClick={() => openEditDialog(combo)}
                                            >
                                                <Edit className="w-4 h-4" />
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                className="text-gray-400 hover:text-red-500"
                                                onClick={() => { setSelectedCombo(combo); setDeleteDialogOpen(true); }}
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </Button>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {filteredCombos.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={7} className="text-center py-12 text-gray-500">
                                        <Gift className="w-12 h-12 mx-auto mb-3 opacity-50" />
                                        <p>Nenhum combo cadastrado</p>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>

            {/* Create/Edit Dialog */}
            <Dialog open={createDialogOpen || editDialogOpen} onOpenChange={(open) => {
                if (!open) {
                    setCreateDialogOpen(false);
                    setEditDialogOpen(false);
                    resetForm();
                }
            }}>
                <DialogContent className="bg-[#0a0a0a] border-[#262626] text-white max-w-2xl">
                    <DialogHeader>
                        <DialogTitle className="text-xl font-heading flex items-center gap-2">
                            <Gift className="w-5 h-5 text-rr-yellow-500" />
                            {editDialogOpen ? 'Editar Combo' : 'Novo Combo'}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="py-4 space-y-4 max-h-[60vh] overflow-y-auto">
                        {/* Basic Info */}
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label className="text-gray-400">Nome do Combo *</Label>
                                <Input
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    className="bg-[#121212] border-[#262626] text-white"
                                    placeholder="Ex: Kit Balada"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label className="text-gray-400">Preço Final *</Label>
                                <Input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={formData.price}
                                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                                    className="bg-[#121212] border-[#262626] text-white"
                                    placeholder="0.00"
                                />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label className="text-gray-400">Descrição (opcional)</Label>
                            <Input
                                value={formData.description}
                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                className="bg-[#121212] border-[#262626] text-white"
                                placeholder="Descrição do combo..."
                            />
                        </div>

                        {/* Image Upload */}
                        <div className="space-y-2">
                            <Label className="text-gray-400">Imagem do Combo (opcional)</Label>
                            <div className="flex gap-4">
                                {/* Preview */}
                                <div className="w-24 h-24 bg-[#0a0a0a] rounded-lg border border-[#262626] flex items-center justify-center overflow-hidden">
                                    {imagePreview || formData.image_url ? (
                                        <img 
                                            src={imagePreview || formData.image_url} 
                                            alt="Preview" 
                                            className="w-full h-full object-cover"
                                        />
                                    ) : (
                                        <ImageIcon className="w-8 h-8 text-gray-600" />
                                    )}
                                </div>
                                {/* Upload Controls */}
                                <div className="flex-1 flex flex-col justify-center gap-2">
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept="image/jpeg,image/png,image/webp"
                                        onChange={handleImageSelect}
                                        className="hidden"
                                        id="combo-image-upload"
                                    />
                                    <Button
                                        type="button"
                                        variant="outline"
                                        className="w-full border-[#262626] text-gray-300 hover:bg-[#1a1a1a]"
                                        onClick={() => fileInputRef.current?.click()}
                                        disabled={uploading}
                                    >
                                        {uploading ? (
                                            <>
                                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                                Enviando...
                                            </>
                                        ) : (
                                            <>
                                                <Upload className="w-4 h-4 mr-2" />
                                                Escolher Imagem
                                            </>
                                        )}
                                    </Button>
                                    {(imagePreview || formData.image_url) && (
                                        <Button
                                            type="button"
                                            variant="outline"
                                            className="w-full border-red-500/50 text-red-400 hover:bg-red-500/10"
                                            onClick={removeImage}
                                        >
                                            <X className="w-4 h-4 mr-2" />
                                            Remover
                                        </Button>
                                    )}
                                    <p className="text-[10px] text-gray-500">JPG, PNG ou WebP. Máx 2MB</p>
                                </div>
                            </div>
                        </div>

                        {/* Product Search */}
                        <div className="space-y-2">
                            <Label className="text-gray-400">Adicionar Produtos</Label>
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                                <Input
                                    value={productSearch}
                                    onChange={(e) => setProductSearch(e.target.value)}
                                    className="pl-10 bg-[#121212] border-[#262626] text-white"
                                    placeholder="Buscar produto para adicionar..."
                                />
                            </div>
                            {productSearch && (
                                <ScrollArea className="h-40 border border-[#262626] rounded-md">
                                    <div className="p-2 space-y-1">
                                        {filteredProducts.slice(0, 10).map(product => (
                                            <div
                                                key={product.id}
                                                className="flex items-center justify-between p-2 hover:bg-[#1a1a1a] rounded cursor-pointer"
                                                onClick={() => addProductToCombo(product)}
                                            >
                                                <div>
                                                    <p className="text-white text-sm">{product.name}</p>
                                                    <p className="text-xs text-gray-500">Custo: {formatCurrency(product.cost_price)}</p>
                                                </div>
                                                <Plus className="w-4 h-4 text-rr-yellow-500" />
                                            </div>
                                        ))}
                                        {filteredProducts.length === 0 && (
                                            <p className="text-center text-gray-500 py-4">Nenhum produto encontrado</p>
                                        )}
                                    </div>
                                </ScrollArea>
                            )}
                        </div>

                        {/* Selected Items */}
                        <div className="space-y-2">
                            <Label className="text-gray-400">Itens do Combo ({formData.items.length})</Label>
                            <div className="border border-[#262626] rounded-md p-2 space-y-2">
                                {formData.items.map((item, idx) => (
                                    <div key={idx} className="flex items-center justify-between p-2 bg-[#121212] rounded">
                                        <div className="flex items-center gap-3">
                                            <Package className="w-4 h-4 text-gray-500" />
                                            <div>
                                                <p className="text-white text-sm">{item.product_name}</p>
                                                <p className="text-xs text-gray-500">Custo: {formatCurrency(item.cost_price * item.quantity)}</p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-8 w-8"
                                                onClick={() => updateItemQuantity(idx, -1)}
                                            >
                                                <Minus className="w-4 h-4" />
                                            </Button>
                                            <span className="w-8 text-center text-white">{item.quantity}</span>
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-8 w-8"
                                                onClick={() => updateItemQuantity(idx, 1)}
                                            >
                                                <Plus className="w-4 h-4" />
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-8 w-8 text-red-500"
                                                onClick={() => removeItem(idx)}
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </Button>
                                        </div>
                                    </div>
                                ))}
                                {formData.items.length === 0 && (
                                    <p className="text-center text-gray-500 py-4">Nenhum produto adicionado</p>
                                )}
                            </div>
                        </div>

                        {/* Summary */}
                        {formData.items.length > 0 && (
                            <div className="p-4 bg-[#121212] rounded-md space-y-2">
                                <div className="flex justify-between text-sm">
                                    <span className="text-gray-400">Custo Total</span>
                                    <span className="text-red-400">{formatCurrency(calculateTotalCost())}</span>
                                </div>
                                <div className="flex justify-between text-sm">
                                    <span className="text-gray-400">Valor Individual (soma)</span>
                                    <span className="text-gray-400">{formatCurrency(calculateTotalValue())}</span>
                                </div>
                                <div className="flex justify-between text-sm">
                                    <span className="text-gray-400">Preço do Combo</span>
                                    <span className="text-white font-bold">{formatCurrency(parseFloat(formData.price) || 0)}</span>
                                </div>
                                <div className="flex justify-between text-sm pt-2 border-t border-[#262626]">
                                    <span className="text-gray-400">Lucro Estimado</span>
                                    <span className={`font-bold ${(parseFloat(formData.price) || 0) - calculateTotalCost() > 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                                        {formatCurrency((parseFloat(formData.price) || 0) - calculateTotalCost())}
                                    </span>
                                </div>
                                <div className="flex justify-between text-sm">
                                    <span className="text-gray-400">Economia Cliente</span>
                                    <span className="text-blue-500">
                                        {formatCurrency(calculateTotalValue() - (parseFloat(formData.price) || 0))}
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => { setCreateDialogOpen(false); setEditDialogOpen(false); resetForm(); }}
                            className="border-[#262626] text-gray-400"
                        >
                            Cancelar
                        </Button>
                        <Button
                            onClick={editDialogOpen ? handleEdit : handleCreate}
                            disabled={processing}
                            className="bg-rr-yellow-500 hover:bg-rr-yellow-400 text-black font-bold"
                        >
                            {processing ? <Loader2 className="w-4 h-4 animate-spin" /> : (editDialogOpen ? 'Salvar' : 'Criar Combo')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                <DialogContent className="bg-[#0a0a0a] border-[#262626] text-white">
                    <DialogHeader>
                        <DialogTitle className="text-xl font-heading flex items-center gap-2">
                            <AlertTriangle className="w-5 h-5 text-amber-500" />
                            Desativar Combo
                        </DialogTitle>
                        <DialogDescription className="text-gray-400">
                            Tem certeza que deseja desativar o combo "{selectedCombo?.name}"?
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setDeleteDialogOpen(false)}
                            className="border-[#262626] text-gray-400"
                        >
                            Cancelar
                        </Button>
                        <Button
                            onClick={handleDelete}
                            disabled={processing}
                            className="bg-red-500 hover:bg-red-400 text-white"
                        >
                            {processing ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Desativar'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default CombosPage;
